/**
 * Application Routes Configuration
 * Centralized route definitions with React Router v7
 */
import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { AppLayout } from './components/templates/AppLayout';
import { AuthLayout } from './components/templates/AuthLayout';
import { RouteErrorBoundary } from './components/templates/RouteErrorBoundary';
import { NotFound, ServerError, Forbidden } from './pages/errors';
import { LoadingSpinner } from './components/atoms/LoadingSpinner';

// Lazy load page components for better performance
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.Dashboard })));
const PriceAnalysis = lazy(() => import('./components/analysis/PriceAnalysis').then(m => ({ default: m.PriceAnalysis })));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage').then(m => ({ default: m.ProductDetailPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const WorkflowPage = lazy(() => import('./components/workflow/WorkflowRouter').then(m => ({ default: m.WorkflowRouter })));
const ProductDiscovery = lazy(() => import('./components/discovery/ProductDiscovery').then(m => ({ default: m.ProductDiscovery })));
const ContentDiscovery = lazy(() => import('./components/discovery/ContentDiscovery').then(m => ({ default: m.ContentDiscovery })));
const SellerOverviewPage = lazy(() => import('./pages/SellerOverviewPage').then(m => ({ default: m.SellerOverview })));
const MyPricingPage = lazy(() => import('./pages/MyPricingPage').then(m => ({ default: m.MyPricing })));
const AIAdvisorPage = lazy(() => import('./pages/AIAdvisorPage').then(m => ({ default: m.AIAdvisor })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.Settings })));
const RoadmapPage = lazy(() => import('./pages/RoadmapPage').then(m => ({ default: m.Roadmap })));
const BillingPage = lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
const SchedulesPage = lazy(() => import('./pages/SchedulesPage').then(m => ({ default: m.SchedulesPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.Login })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPassword })));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center h-full">
    <LoadingSpinner />
  </div>
);

// Wrap lazy components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

// Route configuration
export const router = createBrowserRouter([
  // Auth routes (no sidebar)
  {
    path: '/login',
    element: <AuthLayout />,
    errorElement: <ServerError />,
    children: [
      { index: true, element: withSuspense(LoginPage) }
    ]
  },
  // Password reset route (no sidebar)
  {
    path: '/reset-password',
    element: <AuthLayout />,
    errorElement: <ServerError />,
    children: [
      { index: true, element: withSuspense(ResetPasswordPage) }
    ]
  },
  // Redirect old register route to login
  {
    path: '/register',
    element: <Navigate to="/login" replace />
  },

  // App routes (with sidebar)
  {
    path: '/',
    element: (
      <RouteErrorBoundary>
        <AppLayout />
      </RouteErrorBoundary>
    ),
    errorElement: <ServerError />,
    children: [
      // Dashboard
      { index: true, element: withSuspense(DashboardPage) },

      // User Profile
      { path: 'profile', element: withSuspense(ProfilePage) },

      // Analysis with product detail
      { path: 'analysis', element: withSuspense(PriceAnalysis) },
      { path: 'analysis/:jobId/:productId', element: withSuspense(ProductDetailPage) },

      // Workflow
      { path: 'workflow', element: withSuspense(WorkflowPage) },

      // Schedules
      { path: 'schedules', element: withSuspense(SchedulesPage) },

      // Discovery with product detail
      { path: 'discovery', element: withSuspense(ProductDiscovery) },
      { path: 'discovery/:jobId/:productId', element: withSuspense(ProductDetailPage) },

      // Content Discovery (keyword-based)
      { path: 'content-discovery', element: withSuspense(ContentDiscovery) },

      // Sellers
      { path: 'sellers', element: withSuspense(SellerOverviewPage) },

      // Pricing
      { path: 'pricing', element: withSuspense(MyPricingPage) },

      // AI Advisor
      { path: 'ai-advisor', element: withSuspense(AIAdvisorPage) },

      // Settings
      { path: 'settings', element: withSuspense(SettingsPage) },

      // Billing (admin)
      { path: 'billing', element: withSuspense(BillingPage) },

      // Roadmap
      { path: 'roadmap', element: withSuspense(RoadmapPage) },

      // Error pages
      { path: 'forbidden', element: <Forbidden /> },
      { path: '500', element: <ServerError /> },
    ]
  },

  // Catch-all 404
  { path: '*', element: <NotFound /> }
]);

// Route paths for type safety
export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/',
  PROFILE: '/profile',
  ANALYSIS: '/analysis',
  ANALYSIS_DETAIL: (jobId: string, productId: string) => `/analysis/${jobId}/${productId}`,
  WORKFLOW: '/workflow',
  DISCOVERY: '/discovery',
  DISCOVERY_DETAIL: (jobId: string, productId: string) => `/discovery/${jobId}/${productId}`,
  CONTENT_DISCOVERY: '/content-discovery',
  SELLERS: '/sellers',
  PRICING: '/pricing',
  SCHEDULES: '/schedules',
  AI_ADVISOR: '/ai-advisor',
  SETTINGS: '/settings',
  BILLING: '/billing',
  ROADMAP: '/roadmap',
  LOGIN: '/login',
} as const;
